/**
 * Cost Explorer Service
 * Calls the Cost Explorer Lambda API for real cost data
 * Falls back to mock data if the API is not configured
 */

import { CostData } from '../types';

export class CostExplorerService {
  private static instance: CostExplorerService | null = null;
  private apiUrl: string | null = null;
  private adminToken: string | null = null;

  static getInstance(): CostExplorerService {
    if (!CostExplorerService.instance) {
      CostExplorerService.instance = new CostExplorerService();
    }
    return CostExplorerService.instance;
  }

  constructor() {
    this.apiUrl = process.env.REACT_APP_COST_EXPLORER_API || null;
    this.adminToken = process.env.REACT_APP_ADMIN_API_TOKEN || null;
  }

  /**
   * Check if the Cost Explorer API is configured
   */
  isConfigured(): boolean {
    return !!this.apiUrl;
  }

  /**
   * Fetch cost data from the Lambda API
   */
  private async fetchFromApi(period: string = '30d'): Promise<any | null> {
    if (!this.apiUrl) return null;

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (this.adminToken) {
        headers['X-Admin-Token'] = this.adminToken;
      }

      const response = await fetch(`${this.apiUrl}?period=${period}`, { headers });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Get current month's costs (total and by service)
   */
  async getCurrentMonthCosts(): Promise<{ total: number; byService: { service: string; amount: number }[] }> {
    const data = await this.fetchFromApi('30d');
    if (data) {
      return {
        total: data.totalCost,
        byService: data.topServices.map((s: any) => ({ service: s.service, amount: s.cost }))
      };
    }
    return this.getMockCurrentMonthCosts();
  }

  /**
   * Get daily costs for the last N days
   */
  async getDailyCosts(days: number = 30): Promise<{ date: string; amount: number }[]> {
    const period = days <= 7 ? '7d' : days <= 30 ? '30d' : '90d';
    const data = await this.fetchFromApi(period);
    if (data && data.dailyTrend) {
      return data.dailyTrend.map((d: any) => ({ date: d.date, amount: d.total }));
    }
    return this.getMockDailyCosts(days);
  }

  /**
   * Get cost forecast for current month
   */
  async getCostForecast(): Promise<number> {
    const data = await this.fetchFromApi('30d');
    if (data) {
      // Estimate: extrapolate current daily average to end of month
      const now = new Date();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const daysSoFar = now.getDate();
      const dailyAvg = data.totalCost / Math.max(daysSoFar, 1);
      return Math.round(dailyAvg * daysInMonth * 100) / 100;
    }
    return this.getMockForecast();
  }

  /**
   * Get previous month's total cost
   */
  async getPreviousMonthCosts(): Promise<number> {
    // For previous month, we'd need a separate API call
    // For now, estimate from 30d data
    const data = await this.fetchFromApi('30d');
    if (data) {
      return Math.round(data.totalCost * 0.9 * 100) / 100; // Rough estimate
    }
    return this.getMockPreviousMonth();
  }

  /**
   * Get all cost data aggregated
   */
  async getAllCostData(): Promise<CostData> {
    const [currentMonth, dailyCosts, forecast, previousMonth] = await Promise.all([
      this.getCurrentMonthCosts(),
      this.getDailyCosts(30),
      this.getCostForecast(),
      this.getPreviousMonthCosts(),
    ]);

    const serviceColors: Record<string, string> = {
      'Amazon CloudFront': '#FF9900',
      'Amazon DynamoDB': '#4053D6',
      'AWS Lambda': '#FF9900',
      'Amazon API Gateway': '#A166FF',
      'Amazon S3': '#3F8624',
      'Amazon Cognito': '#DD344C',
      'AWS CloudFormation': '#FF4F8B',
      'Amazon CloudWatch': '#FF6B6B',
    };

    const serviceBreakdown = currentMonth.byService.map(s => ({
      service: s.service,
      amount: s.amount,
      color: serviceColors[s.service] || '#9E9E9E',
    }));

    const changePercent = previousMonth > 0
      ? Math.round(((currentMonth.total - previousMonth) / previousMonth) * 100)
      : 0;

    return {
      totalCurrentMonth: currentMonth.total,
      dailyCosts,
      serviceBreakdown,
      forecast,
      previousMonth,
      changePercent,
    };
  }

  // --- Mock data for demo when API is not configured ---

  private getMockCurrentMonthCosts() {
    return {
      total: 47.82,
      byService: [
        { service: 'Amazon CloudFront', amount: 18.50 },
        { service: 'Amazon DynamoDB', amount: 12.30 },
        { service: 'AWS Lambda', amount: 8.45 },
        { service: 'Amazon API Gateway', amount: 4.20 },
        { service: 'Amazon S3', amount: 2.15 },
        { service: 'Amazon Cognito', amount: 1.80 },
        { service: 'Amazon CloudWatch', amount: 0.42 },
      ],
    };
  }

  private getMockDailyCosts(days: number) {
    const costs: { date: string; amount: number }[] = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const base = 1.2 + Math.random() * 0.8;
      const dayOfWeek = date.getDay();
      const multiplier = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.7 : 1.0;
      costs.push({
        date: date.toISOString().split('T')[0],
        amount: Math.round(base * multiplier * 100) / 100,
      });
    }
    return costs;
  }

  private getMockForecast() {
    return 52.40;
  }

  private getMockPreviousMonth() {
    return 43.15;
  }
}
